/**
 * The owner's goals: a create line, the open goals ranked by drag (rank =
 * priority; the page persists the order and rolls back on failure) and the
 * achieved / retired archive below.
 */
import { useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { clsx } from "clsx";

import type { FocusAudience, FocusGoal, GoalCreateBody, GoalPatchBody, SeatRef } from "../../api/focus";
import { AUDIENCES } from "../../lib/focus";
import { parseDuration } from "../../lib/focusTime";
import { GoalRow, SortableGoalRow } from "./GoalRow";

export function GoalList({
  open,
  archived,
  seats,
  busyId,
  creating,
  onCreate,
  onPatch,
  onReorder,
}: {
  /** Active + paused, in rank order. */
  open: FocusGoal[];
  archived: FocusGoal[];
  seats: SeatRef[];
  busyId: string | null;
  creating: boolean;
  /** Resolves on success, rejects on failure (the line keeps its text). */
  onCreate: (body: Omit<GoalCreateBody, "request_id">) => Promise<void>;
  onPatch: (goal: FocusGoal, patch: GoalPatchBody) => Promise<unknown>;
  onReorder: (goalIds: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(ev: DragEndEvent) {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    const ids = open.map((g) => g.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(ids, from, to));
  }

  return (
    <div className="space-y-6">
      <NewGoalLine creating={creating} onCreate={onCreate} />

      <div className="panel panel--flush">
        <div className="panel-header">
          <h3>Open goals</h3>
          <span className="text-xs text-ink-low font-mono">drag ⠿ to rank – #1 comes first</span>
        </div>
        {open.length === 0 ? (
          <div className="empty-state">
            <div className="title">No open goals</div>
            <p className="hint">Add one above – a title is enough to start planning time for it.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={open.map((g) => g.id)} strategy={verticalListSortingStrategy}>
              <ul className="goal-list">
                {open.map((goal) => (
                  <SortableGoalRow
                    key={goal.id}
                    goal={goal}
                    seats={seats}
                    busy={busyId === goal.id}
                    onPatch={onPatch}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {archived.length > 0 ? (
        <details className="panel panel--flush goal-archive">
          <summary className="panel-header">
            <h3>Achieved & retired</h3>
            <span className="text-xs text-ink-low font-mono">{archived.length}</span>
          </summary>
          <ul className="goal-list">
            {archived.map((goal) => (
              <li key={goal.id}>
                <GoalRow goal={goal} seats={seats} busy={busyId === goal.id} onPatch={onPatch} />
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function NewGoalLine({
  creating,
  onCreate,
}: {
  creating: boolean;
  onCreate: (body: Omit<GoalCreateBody, "request_id">) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [audience, setAudience] = useState<FocusAudience>("me");
  const [budget, setBudget] = useState("");
  const minutes = budget.trim() ? parseDuration(budget) : null;
  const budgetInvalid = budget.trim() !== "" && (minutes === null || minutes > 10080);

  async function submit() {
    if (!title.trim() || budgetInvalid) return;
    try {
      await onCreate({ title: title.trim(), audience, weekly_minutes: minutes });
      setTitle("");
      setBudget("");
      setAudience("me");
    } catch {
      // The page toasts; the line keeps its text for a retry.
    }
  }

  return (
    <form
      className="panel goal-create"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex-1 min-w-0">
        <label className="label" htmlFor="new-goal-title">
          New goal
        </label>
        <input
          id="new-goal-title"
          className="input"
          maxLength={255}
          placeholder="What do you want to move forward?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <span className="label">Who</span>
        <div className="segmented" role="radiogroup" aria-label="Audience">
          {AUDIENCES.map((a) => (
            <button
              key={a.value}
              type="button"
              role="radio"
              aria-checked={audience === a.value}
              title={a.hint}
              className={clsx("segmented-item", audience === a.value && "is-active")}
              onClick={() => setAudience(a.value)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-28">
        <label className="label" htmlFor="new-goal-budget">
          Per week
        </label>
        <input
          id="new-goal-budget"
          className={clsx("input font-mono", budgetInvalid && "is-invalid")}
          placeholder="4h"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          title="90, 1:30 or 1.5h"
        />
      </div>
      <button type="submit" className="btn btn-main" disabled={creating || !title.trim() || budgetInvalid}>
        {creating ? "Adding…" : "Add goal"}
      </button>
    </form>
  );
}
